import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const CONFIRMATION_PHRASE = "DELETE ABSENCE HISTORY";

export default function ClearHistoryButton({ onCleared }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshCount = async () => {
    try {
      const { data } = await api.get("/admin/holiday-requests/count");
      setCount(data.count);
    } catch { /* ignore */ }
  };
  useEffect(() => { refreshCount(); }, []);

  const open_ = () => { setPhrase(""); refreshCount(); setOpen(true); };

  const doClear = async () => {
    if (phrase !== CONFIRMATION_PHRASE) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/admin/holiday-requests/clear-all", {
        params: { confirmation: CONFIRMATION_PHRASE },
      });
      toast.success(`Deleted ${data.deleted_requests} absence notification(s)`);
      setOpen(false);
      setPhrase("");
      setCount(0);
      onCleared?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear history");
    } finally {
      setBusy(false);
    }
  };

  const isEmpty = count === 0;

  return (
    <>
      <Button
        data-testid="clear-history-btn"
        variant="outline"
        size="sm"
        onClick={open_}
        className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
        title={isEmpty ? "No absence notifications to delete" : "Delete all absence notification history"}
      >
        <Trash2 className="w-3.5 h-3.5"/>
        {isEmpty ? "No history to delete" : "Clear request history"}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="clear-history-dialog">
          <DialogHeader>
            <div className="w-10 h-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mb-2">
              <ShieldAlert className="w-5 h-5"/>
            </div>
            <DialogTitle className="font-heading">Clear all absence notification history?</DialogTitle>
            <DialogDescription className="leading-relaxed pt-1">
              This will permanently delete <span className="font-semibold text-foreground" data-testid="clear-history-count">{count ?? "…"}</span> absence
              notification{count === 1 ? "" : "s"} and their related email logs.<br/>
              Contractors, supervisors, departments, codes and settings will <strong>not</strong> be touched.
            </DialogDescription>
          </DialogHeader>

          {isEmpty ? (
            <div className="status-cancelled rounded-md p-3 text-sm" data-testid="clear-history-empty">
              No absence notifications to delete.
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground font-semibold">
                Type <span className="font-mono text-foreground">{CONFIRMATION_PHRASE}</span> to confirm
              </Label>
              <Input
                data-testid="clear-history-phrase-input"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={CONFIRMATION_PHRASE}
                className="font-mono"
                autoFocus
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="clear-history-cancel">Cancel</Button>
            <Button
              data-testid="clear-history-confirm"
              variant="destructive"
              onClick={doClear}
              disabled={busy || isEmpty || phrase !== CONFIRMATION_PHRASE}
              className="gap-1.5"
            >
              <Trash2 className="w-4 h-4"/>
              {busy ? "Deleting…" : "Delete history"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
